/**
 * CCPL-8277 — client-side proxy route derivation (UI only).
 * Mirrors media-server-admin-ui dashboard.html and mediaServerLive03/services/proxyRoutes.js.
 */

/** Supported version folders immediately after the top-level directory (2nd path segment). */
export const KNOWN_PHP_VERSIONS = [
  { segment: 'php5', target: 'PHP5' },
  { segment: 'php8', target: 'PHP8' },
  { segment: 'php9', target: 'PHP9' },
];

export const PROXY_TARGET_OPTIONS = KNOWN_PHP_VERSIONS.map(({ target }) => target);

export const REROUTE_OPTIONS = [
  { value: '', label: 'None' },
  ...KNOWN_PHP_VERSIONS.map(({ segment }) => ({ value: segment, label: segment })),
];

const KNOWN_PHP_VERSION_BY_SEGMENT = Object.fromEntries(
  KNOWN_PHP_VERSIONS.map((version) => [version.segment, version])
);

/** Mirror runtimeConfigBuilder.normalizeRoutePrefix — no leading/trailing slashes. */
export const normalizeRoutePrefix = (input) => {
  if (!input || typeof input !== 'string') {
    return '';
  }
  return input
    .trim()
    .replace(/\\/g, '/')
    .split('/')
    .filter(Boolean)
    .join('/');
};

/** Public pathname with a single leading slash. */
export const normalizeOriginalRoute = (input) => {
  const normalized = normalizeRoutePrefix(input);
  if (!normalized) {
    return '';
  }
  return `/${normalized}`;
};

export const getRouteSegments = (originalRoute) => (
  normalizeRoutePrefix(originalRoute).split('/').filter(Boolean)
);

/**
 * Returns the PHP version declared at the 2nd path segment, e.g. /ab/php8/app → php8.
 * Only index 1 is valid for version folders used with appendPhpRoute rewriting.
 */
export const getSecondLevelPhpVersion = (originalRoute) => {
  const segments = getRouteSegments(originalRoute);
  if (segments.length < 2) {
    return null;
  }
  return KNOWN_PHP_VERSION_BY_SEGMENT[segments[1].toLowerCase()] || null;
};

/** Detect php5/php8/php9 appearing deeper than the 2nd segment — invalid for proxy mapping. */
export const findMisplacedPhpVersionSegment = (originalRoute) => {
  const segments = getRouteSegments(originalRoute);
  for (let index = 0; index < segments.length; index += 1) {
    const segment = segments[index].toLowerCase();
    const match = KNOWN_PHP_VERSION_BY_SEGMENT[segment];
    if (match && index !== 1) {
      return match.segment;
    }
  }
  return null;
};

export const isProxyVersionInferred = (originalRoute) => Boolean(getSecondLevelPhpVersion(originalRoute));

/** Reject php-prefixed folder names that are not exactly php5, php8, or php9. */
export const validateSecondSegmentPhpFolderName = (originalRoute) => {
  const segments = getRouteSegments(originalRoute);
  if (segments.length < 2) {
    return null;
  }

  const secondSegment = segments[1];
  const second = secondSegment.toLowerCase();
  if (KNOWN_PHP_VERSION_BY_SEGMENT[second]) {
    return null;
  }

  if (/^php/i.test(second)) {
    return (
      `2nd path segment "${secondSegment}" is not a valid PHP version folder. `
      + 'Use exactly php5, php8, or php9 (e.g. /ab/php8/myapp, not /ab/php8test).'
    );
  }

  return null;
};

/** Reroute php5/php8/php9 requires that exact folder at the 2nd path segment. */
export const validateRerouteRequiresSecondSegmentMatch = (formState) => {
  const reroute = normalizeAppendPhpRoute(formState.reroute);
  if (!reroute) {
    return null;
  }

  const inferred = getSecondLevelPhpVersion(formState.originalRoute);
  if (!inferred) {
    return (
      `Reroute "${reroute}" requires Original Route to declare /${reroute}/ as the exact 2nd path segment `
      + `(e.g. /ab/${reroute}/myapp).`
    );
  }

  if (inferred.segment !== reroute) {
    return (
      `Reroute must be "${inferred.segment}" to match the PHP version folder in Original Route. `
      + 'This cannot be changed for security.'
    );
  }

  return null;
};

export const publicPathFromPrefix = (prefix) => normalizeOriginalRoute(prefix);

/** Stored DynamoDB prefix — normalized public Proxy Route (directory or file path). */
export const deriveStoredPrefix = (publicRoute) => normalizeRoutePrefix(publicRoute);

export const normalizeAppendPhpRoute = (appendPhpRoute) => {
  if (appendPhpRoute === false || appendPhpRoute == null || appendPhpRoute === '') {
    return '';
  }
  return String(appendPhpRoute).trim();
};

/** Mirror services/proxyRoutes.js computeRewriteUri (legacy/disk path from public path). */
export const computeRewriteUri = (pathname, appendPhpRoute) => {
  if (!appendPhpRoute || typeof appendPhpRoute !== 'string') {
    return null;
  }
  let cleanRoute = appendPhpRoute.trim();
  while (cleanRoute.startsWith('/')) {
    cleanRoute = cleanRoute.slice(1);
  }
  while (cleanRoute.endsWith('/')) {
    cleanRoute = cleanRoute.slice(0, -1);
  }
  if (!cleanRoute) {
    return null;
  }

  const slashIdx = pathname.indexOf('/', 1);
  if (slashIdx === -1) {
    return pathname;
  }

  const nextSlash = pathname.indexOf('/', slashIdx + 1);
  const firstPhysicalSegment = pathname.slice(
    slashIdx + 1,
    nextSlash === -1 ? pathname.length : nextSlash
  );
  if (firstPhysicalSegment.toLowerCase() === cleanRoute.toLowerCase()) {
    return pathname;
  }

  return pathname.slice(0, slashIdx) + '/' + cleanRoute + pathname.slice(slashIdx);
};

/**
 * New public browser path after migration — inverse of computeRewriteUri.
 * Legacy /pegasus/php8/test/php8.php + append php8 → /pegasus/test/php8.php
 */
export const computePublicRouteFromLegacy = (pathname, appendPhpRoute) => {
  if (!pathname) {
    return '';
  }
  if (!appendPhpRoute || typeof appendPhpRoute !== 'string') {
    return pathname;
  }
  let cleanRoute = appendPhpRoute.trim();
  while (cleanRoute.startsWith('/')) {
    cleanRoute = cleanRoute.slice(1);
  }
  while (cleanRoute.endsWith('/')) {
    cleanRoute = cleanRoute.slice(0, -1);
  }
  if (!cleanRoute) {
    return pathname;
  }

  const slashIdx = pathname.indexOf('/', 1);
  if (slashIdx === -1) {
    return pathname;
  }

  const nextSlash = pathname.indexOf('/', slashIdx + 1);
  const firstSegment = pathname.slice(
    slashIdx + 1,
    nextSlash === -1 ? pathname.length : nextSlash
  );
  if (firstSegment.toLowerCase() !== cleanRoute.toLowerCase()) {
    return pathname;
  }

  const rest = nextSlash === -1 ? '' : pathname.slice(nextSlash);
  return pathname.slice(0, slashIdx) + rest;
};

export const computeLegacyRouteFromPrefix = (prefix, appendPhpRoute) => {
  const publicPath = publicPathFromPrefix(prefix);
  if (!publicPath) {
    return '';
  }
  const apr = normalizeAppendPhpRoute(appendPhpRoute);
  if (!apr) {
    return publicPath;
  }
  return computeRewriteUri(publicPath, apr) || publicPath;
};

export const formatProxyRouteDisplay = (originalRoute, appendPhpRoute) => {
  const legacyPath = normalizeOriginalRoute(originalRoute);
  if (!legacyPath) {
    return '';
  }
  const apr = normalizeAppendPhpRoute(appendPhpRoute);
  return computePublicRouteFromLegacy(legacyPath, apr);
};

export const formatRerouteDisplay = (appendPhpRoute) => {
  const apr = normalizeAppendPhpRoute(appendPhpRoute);
  return apr || '\u2014';
};

export const isCurriculumPath = (webPath) => {
  const path = normalizeOriginalRoute(webPath);
  return path.startsWith('/curriculum') || path.startsWith('/ov2/curriculum');
};

export const buildLiveUrl = (webPath, { liveUrlBase, liveUrlBaseCurriculum } = {}) => {
  const pathname = normalizeOriginalRoute(webPath);
  if (!pathname) {
    return null;
  }
  const bases = [];
  if (liveUrlBase) {
    bases.push(String(liveUrlBase).replace(/\/+$/, ''));
  }
  if (isCurriculumPath(pathname) && liveUrlBaseCurriculum) {
    bases.push(String(liveUrlBaseCurriculum).replace(/\/+$/, ''));
  }
  const base = bases[0];
  if (!base) {
    return null;
  }
  return `${base}${pathname}`;
};

export const getDefaultProxyFormState = () => ({
  originalRoute: '',
  reroute: '',
  target: 'PHP8',
  proxyRoute: '',
});

export const syncProxyRouteField = (formState) => ({
  ...formState,
  proxyRoute: formatProxyRouteDisplay(formState.originalRoute, formState.reroute),
});

/** Auto-select target/reroute when Original Route declares php5/php8/php9 at the 2nd segment. */
export const applyOriginalRouteInference = (formState) => {
  const inferred = getSecondLevelPhpVersion(formState.originalRoute);
  const next = inferred
    ? { ...formState, reroute: inferred.segment, target: inferred.target }
    : formState;
  return syncProxyRouteField(next);
};

export const validateOriginalRoute = (originalRoute) => {
  const raw = typeof originalRoute === 'string' ? originalRoute.trim() : '';
  if (!raw) {
    return 'Original Route is required.';
  }
  if (raw.includes('?') || raw.includes('#')) {
    return 'Query string and fragment are not allowed in Original Route.';
  }
  const path = normalizeOriginalRoute(originalRoute);
  if (!path) {
    return 'Original Route is required.';
  }
  return null;
};

export const validatePhpVersionPlacement = (originalRoute) => {
  const originalError = validateOriginalRoute(originalRoute);
  if (originalError) {
    return originalError;
  }

  const misplaced = findMisplacedPhpVersionSegment(originalRoute);
  if (misplaced) {
    return (
      `PHP version folder "${misplaced}" must be the 2nd path segment `
      + `(e.g. /ab/${misplaced}/myapp). It cannot appear deeper in the route.`
    );
  }

  const invalidSecondSegment = validateSecondSegmentPhpFolderName(originalRoute);
  if (invalidSecondSegment) {
    return invalidSecondSegment;
  }

  return null;
};

export const validatePhpVersionConsistency = (formState) => {
  const inferred = getSecondLevelPhpVersion(formState.originalRoute);
  if (!inferred) {
    return null;
  }

  const reroute = normalizeAppendPhpRoute(formState.reroute);
  const target = String(formState.target || '').trim();

  if (reroute !== inferred.segment) {
    const rerouteLabel = reroute || 'None';
    return (
      `Reroute must be "${inferred.segment}" to match the PHP version in Original Route `
      + `(not "${rerouteLabel}"). This cannot be changed for security.`
    );
  }

  if (target !== inferred.target) {
    return (
      `Target Server must be "${inferred.target}" to match the PHP version in Original Route. `
      + 'This cannot be changed for security.'
    );
  }

  return null;
};

export const validateRerouteValue = (reroute) => {
  const value = normalizeAppendPhpRoute(reroute);
  if (!value) {
    return null;
  }
  if (value.includes('..') || value.includes('\\') || value.startsWith('/')) {
    return 'Reroute must be a relative segment (e.g. php8), without ../ or leading /.';
  }
  return null;
};

export const validateProxyForm = (formState, { operation = 'merge' } = {}) => {
  if (operation === 'remove') {
    if (!formState.prefix?.trim()) {
      return 'Prefix is required to remove a mapping.';
    }
    return null;
  }

  const originalError = validatePhpVersionPlacement(formState.originalRoute);
  if (originalError) {
    return originalError;
  }

  const consistencyError = validatePhpVersionConsistency(formState);
  if (consistencyError) {
    return consistencyError;
  }

  const rerouteError = validateRerouteValue(formState.reroute);
  if (rerouteError) {
    return rerouteError;
  }

  const reroutePlacementError = validateRerouteRequiresSecondSegmentMatch(formState);
  if (reroutePlacementError) {
    return reroutePlacementError;
  }

  if (!formState.target?.trim()) {
    return 'Target is required (e.g. PHP8).';
  }

  const synced = syncProxyRouteField(formState);
  if (!synced.proxyRoute) {
    return 'Could not derive Proxy Route from Original Route.';
  }

  const prefix = deriveStoredPrefix(synced.proxyRoute);
  if (!prefix) {
    return 'Could not derive application prefix from Proxy Route.';
  }

  return null;
};

/** Build API entry for merge from operator form fields. */
export const buildProxyEntryFromForm = (formState) => {
  const synced = syncProxyRouteField(formState);
  const prefix = deriveStoredPrefix(synced.proxyRoute);
  const entry = {
    prefix,
    target: String(formState.target || '').trim(),
  };
  const apr = normalizeAppendPhpRoute(formState.reroute);
  entry.appendPhpRoute = apr ? apr : false;
  return entry;
};

export const buildProxyRemoveEntry = (prefix) => ({
  prefix: normalizeRoutePrefix(prefix),
});

export const getProxyEntryKey = (entry = {}) => normalizeRoutePrefix(entry.prefix);

export const getProxyExpectedVersion = (metadataItem, document) => {
  if (metadataItem && Number.isInteger(metadataItem.version)) {
    return metadataItem.version;
  }
  if (document && Number.isInteger(document.version)) {
    return document.version;
  }
  if (document == null) {
    return null;
  }
  return 0;
};

export const prefixToDisplayRoute = (prefix) => {
  const normalized = normalizeRoutePrefix(prefix);
  return normalized ? `/${normalized}` : '';
};

/** Path segment(s) that distinguish a child prefix from its parent, e.g. php8.php. */
export const getNestedRouteSuffix = (parentPrefix, childPrefix) => {
  const parent = normalizeRoutePrefix(parentPrefix);
  const child = normalizeRoutePrefix(childPrefix);
  if (parent && child.startsWith(`${parent}/`)) {
    return child.slice(parent.length + 1);
  }
  return child;
};

const formatConflictValue = (value) => {
  const normalized = normalizeAppendPhpRoute(value);
  return normalized || 'None';
};

/** Structured overlap rows for the confirmation modal. */
export const formatOverlapDetails = (overlaps = []) => {
  if (!Array.isArray(overlaps)) {
    return [];
  }

  return overlaps.map((overlap) => {
    const parentRoute = prefixToDisplayRoute(overlap.parent);
    const childRoute = prefixToDisplayRoute(overlap.child);
    const winnerRoute = prefixToDisplayRoute(overlap.winner);
    const nestedName = getNestedRouteSuffix(overlap.parent, overlap.child);
    const isNested = nestedName !== normalizeRoutePrefix(overlap.child);

    return {
      parentRoute,
      childRoute,
      nestedName,
      isNested,
      winnerRoute,
      targetConflict: overlap.conflicts?.target
        ? {
          parent: overlap.conflicts.target.parent || 'None',
          child: overlap.conflicts.target.child || 'None',
        }
        : null,
      rerouteConflict: overlap.conflicts?.appendPhpRoute
        ? {
          parent: formatConflictValue(overlap.conflicts.appendPhpRoute.parent),
          child: formatConflictValue(overlap.conflicts.appendPhpRoute.child),
        }
        : null,
    };
  });
};

export const formatOverlapSummary = (overlaps = []) => {
  const details = formatOverlapDetails(overlaps);
  if (!details.length) {
    return (
      'This prefix overlaps an existing parent/child route with conflicting target or reroute. '
      + 'The longest matching prefix wins.'
    );
  }

  return details
    .map((detail, index) => {
      const lines = [];
      const heading = details.length > 1 ? `Conflict ${index + 1}` : 'Conflicting routes';
      lines.push(heading);
      lines.push(`Broader route: ${detail.parentRoute}`);
      lines.push(
        detail.isNested
          ? `Overlapping route: ${detail.nestedName} (under ${detail.parentRoute})`
          : `Overlapping route: ${detail.childRoute}`
      );
      if (detail.targetConflict) {
        lines.push(
          `Target conflict: ${detail.targetConflict.parent} vs ${detail.targetConflict.child}`
        );
      }
      if (detail.rerouteConflict) {
        lines.push(
          `Reroute conflict: ${detail.rerouteConflict.parent} vs ${detail.rerouteConflict.child}`
        );
      }
      lines.push(`Longer match wins: ${detail.winnerRoute}`);
      return lines.join('\n');
    })
    .join('\n\n');
};

/** Map DynamoDB proxy document to grid row objects. */
export const proxyRowsFromDocument = (document) => {
  const migrated = document?.data?.migratedPrefixes || {};
  return Object.keys(migrated)
    .sort()
    .map((prefix) => {
      const entry = migrated[prefix] || {};
      return {
        prefix,
        target: entry.target || '',
        appendPhpRoute: entry.appendPhpRoute,
        migratedAt: entry.migratedAt,
        originalRoute: computeLegacyRouteFromPrefix(prefix, entry.appendPhpRoute),
        proxyRoute: publicPathFromPrefix(prefix),
      };
    });
};

export const buildProxyEditPrefill = (row) => ({
  isEdit: true,
  editKey: row.prefix,
  action: 'merge',
  formState: {
    originalRoute: row.originalRoute || computeLegacyRouteFromPrefix(row.prefix, row.appendPhpRoute),
    reroute: normalizeAppendPhpRoute(row.appendPhpRoute),
    target: row.target || 'PHP8',
    proxyRoute: row.proxyRoute || publicPathFromPrefix(row.prefix),
    prefix: row.prefix,
  },
});
